import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import AdsBanner, { BannerPosition } from "@components/ads-banner/ads-banner";
import Button from "@components/button/button";
import CapitanGreatingSVG from "@components/icons/capitan-greating-svg";
import { useMetaTags } from "@components/meta-tags/hooks/use-meta-tags";
import WrapperAdsBanner from "@components/wrapper-ads-banner/WrapperAdsBanner";

import { useAppDispatch } from "@hooks/use-store";
import {
  currentCoinsUpload,
  progressUpload,
  setIsAdsScriptsReloaded,
} from "@store/app/app.slice";
import { formatUrlWithLocale } from "@helpers/format-url-with-locale";
import { calculatePercents, speed2CurrentUnits } from "@helpers/helpers";
import { ExerciseCompleteData } from "@typings/global.types";

import { EXTERNAL_ROUTES } from "../../routes/routes.constant";

function TestStringResults() {
  const [testStringResultsData, setTestStringResultsData] =
    useState<ExerciseCompleteData | null>(null);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const sessionStorageData = window.sessionStorage.getItem("testStringResults");
  const intl = useIntl();
  const { setMetaTags } = useMetaTags();

  useEffect(() => {
    if (!sessionStorageData) {
      dispatch(progressUpload(0));
      dispatch(currentCoinsUpload(0));
      navigate(formatUrlWithLocale("/typing-tutor/"));
    } else {
      setTestStringResultsData(JSON.parse(sessionStorageData));
    }
  }, [dispatch, navigate, sessionStorageData]);

  if (sessionStorageData) {
    setMetaTags({
      title: intl.formatMessage({ id: "meta-title-finish-unauthorized" }),
      description: intl.formatMessage({
        id: "meta-description-finish-unauthorized",
      }),
    });
  }

  useEffect(() => {
    if (testStringResultsData) {
      calculatePercents(
        speed2CurrentUnits(
          Math.round(testStringResultsData.speed),
          testStringResultsData.speedRatio,
        ),
      );
    }
  }, [testStringResultsData]);

  useEffect(() => {
    dispatch(setIsAdsScriptsReloaded(true));
  }, [dispatch]);

  if (!testStringResultsData) {
    return null;
  }

  return (
    <div className='flex flex-col-reverse md:flex-col'>
      <section className='mx-auto max-w-container-xsmall text-center'>
        <CapitanGreatingSVG
          height={100}
          width={100}
          classNameProps='mx-auto my-0 mb-4'
        />
        <h2>
          {speed2CurrentUnits(
            Math.round(testStringResultsData.speed),
            testStringResultsData.speedRatio,
          )}{" "}
          <FormattedMessage
            id='word-per-minute'
            values={{
              count: speed2CurrentUnits(
                Math.round(testStringResultsData.speed),
                testStringResultsData.speedRatio,
              ),
            }}
          />
        </h2>
      </section>
      <div className='mb-3 min-h-[110px] md:mb-4'>
        <WrapperAdsBanner page='test-string-result'>
          <AdsBanner bannerPosition={BannerPosition.TEST_STRING_RESULT} />
        </WrapperAdsBanner>
      </div>
      <section className='mx-auto mb-6 max-w-container-xsmall text-center md:mb-8'>
        <div className='pt-lg flex items-center justify-between'>
          <p>
            <FormattedMessage
              id='you-type-faster'
              values={{
                percent: calculatePercents(testStringResultsData.speed),
              }}
            />
          </p>
        </div>
        <div className='mt-xl'>
          <a
            href={formatUrlWithLocale(EXTERNAL_ROUTES.signup)}
            className='btn btn-primary'
            id='test_string'
            onClick={(e) => {
              document.cookie = `eventCategory=${e.currentTarget.id};path=/`;
            }}
          >
            <FormattedMessage id='improve-typing-speed' />
          </a>
        </div>
      </section>
    </div>
  );
}

export default TestStringResults;
